import { useCallback, useMemo } from "react";
import http from "axiosSetup";
import useFileDownloader from "hooks/useFileDownloader";
import { AxiosError } from "axios";
import type { MuxMeasurementVideo } from "types";

function useRemoteVideoDownloader() {
  const { download, status, error } = useFileDownloader();

  const errorMessage = useMemo(
    () =>
      error instanceof AxiosError
        ? "errors.video_recording_download_error"
        : error instanceof Error
          ? error.message
          : null,
    [error]
  );

  const handleOnRemoteVideoDownload = useCallback(
    async (video: MuxMeasurementVideo, token?: string) => {
      if (video.mp4Ready === false) {
        return download("not_ready.mp4", () => {
          throw new Error("errors.video_recording_not_ready");
        });
      }

      const videoUrl = new URL(`https://stream.mux.com/${video.playbackId}/capped-1080p.mp4`);
      if (token) {
        videoUrl.searchParams.append("token", token);
      }

      const filename = `output-video-recording-${Date.now()}.mp4`;

      await download(filename, async () => {
        const { data } = await http.get<Blob>(String(videoUrl), {
          responseType: "blob",
          headers: { "Content-Type": "video/mp4" },
        });
        return data;
      });
    },
    [download]
  );

  return { handleOnRemoteVideoDownload, status, error: errorMessage };
}

export default useRemoteVideoDownloader;
